import inventory from './data/manufacturer-products.json';

const MANUFACTURERS = ['medline', 'smith-nephew', 'molnlycke', 'solventum', 'urgo', 'hartmann'];
const SOURCE_HOSTS = {
  medline: ['athome.medline.com', 'www.medline.com'],
  'smith-nephew': ['www.smith-nephew.com', 'smith-nephew.stylelabs.cloud'],
  molnlycke: ['www.molnlycke.com', 'static.molnlycke.com'],
  solventum: ['www.solventum.com'],
  urgo: ['www.urgomedical.us', 'cdn.prod.website-files.com'],
  hartmann: ['www.hartmann.info'],
};
const fail = () => { throw new Error('Invalid manufacturer identity directory.'); };
function keys(value, names) {
  if (!value || typeof value !== 'object' || Array.isArray(value)
      || Object.keys(value).length !== names.length
      || names.some(n => !Object.hasOwn(value, n))) fail();
}
function text(value, max = 1500, empty = false) {
  if (typeof value !== 'string' || value.length > max || (!empty && !value.trim())) fail();
}
function list(value, max, min = 1) {
  if (!Array.isArray(value) || value.length < min || value.length > max) fail();
}
function date(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) fail();
  const parsed = new Date(value);
  if (!Number.isFinite(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== value) fail();
}
function sourceUrl(value, manufacturer, optional = false) {
  text(value, 2048, optional);
  if (!value && optional) return;
  let u;
  try { u = new URL(value); } catch { fail(); }
  if (u.protocol !== 'https:' || u.username || u.password || u.port
      || !SOURCE_HOSTS[manufacturer]?.includes(u.hostname)) fail();
}
function freeze(value) {
  Object.values(value).forEach(v => { if (v && typeof v === 'object') freeze(v); });
  return Object.freeze(value);
}
const normalizeDiscovery = value => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

// This boundary admits factual identity comparisons only. It does not manufacture
// ResourceCatalog records, treatment options, IFU steps, coverage, or equivalence.
export function createDirectory(input) {
  keys(input, ['schema_version', 'observed_on', 'market', 'scope', 'manufacturer_names', 'sources', 'products', 'gaps']);
  if (input.schema_version !== 'manufacturer-identity-directory-v1'
      || input.scope !== 'identity_comparison_only' || input.market !== 'US') fail();
  date(input.observed_on);
  keys(input.manufacturer_names, MANUFACTURERS);
  Object.values(input.manufacturer_names).forEach(v => text(v, 80));
  list(input.sources, 100); list(input.products, 1000); list(input.gaps, 100);
  const sources = new Map();
  for (const s of input.sources) {
    keys(s, ['id', 'manufacturer', 'title', 'kind', 'url', 'market', 'observed_on', 'revision', 'locator', 'sha256', 'capture_method', 'discovered_from', 'rights', 'currency_note']);
    if (!MANUFACTURERS.includes(s.manufacturer) || s.market !== input.market || sources.has(s.id)) fail();
    for (const k of ['id', 'title', 'revision', 'locator', 'currency_note']) text(s[k]);
    if (!['product_page', 'application_guide', 'ifu', 'ordering_table'].includes(s.kind)) fail();
    date(s.observed_on);
    if (s.observed_on > input.observed_on || s.rights !== 'factual_summary_and_link_only') fail();
    sourceUrl(s.url, s.manufacturer); sourceUrl(s.discovered_from, s.manufacturer, true);
    if (!['public_http_snapshot', 'web_retrieval'].includes(s.capture_method)) fail();
    if (s.sha256 === null ? s.capture_method !== 'web_retrieval' : !/^[a-f0-9]{64}$/.test(s.sha256)) fail();
    sources.set(s.id, s);
  }
  const byId = new Map();
  function references(row) {
    list(row.source_ids, 20);
    if (new Set(row.source_ids).size !== row.source_ids.length) fail();
    for (const id of row.source_ids) {
      const s = sources.get(id);
      if (!s || s.manufacturer !== row.manufacturer || s.market !== input.market) fail();
    }
  }
  for (const p of input.products) {
    keys(p, ['id', 'manufacturer', 'market', 'sku', 'name', 'variant', 'category', 'size', 'pack', 'source_ids', 'locator', 'notes', 'restrictions_status', 'admission', 'clinical_use_authorized', 'complete_IFU_verified']);
    if (!MANUFACTURERS.includes(p.manufacturer) || p.market !== input.market
        || p.id !== `${p.manufacturer}:${p.market}:${p.sku}` || byId.has(p.id)
        || p.admission !== 'identity_comparison_only' || p.clinical_use_authorized !== false
        || p.complete_IFU_verified !== false) fail();
    for (const k of ['sku', 'name', 'variant', 'category', 'size', 'pack', 'locator']) text(p[k], 500);
    if (!/^[A-Z0-9-]{1,60}$/.test(p.sku)) fail();
    if (!['not_captured', 'partial_source_summary', 'unresolved_source_scope'].includes(p.restrictions_status)) fail();
    references(p); list(p.notes, 20);
    for (const n of p.notes) {
      keys(n, ['context', 'text', 'source_id']); text(n.context, 150); text(n.text);
      if (!p.source_ids.includes(n.source_id)) fail();
    }
    byId.set(p.id, p);
  }
  for (const g of input.gaps) {
    keys(g, ['manufacturer', 'label', 'reason', 'source_ids']);
    if (!MANUFACTURERS.includes(g.manufacturer)) fail();
    text(g.label, 300); text(g.reason); references(g);
  }
  // Copy before freezing; callers retain ownership of their own input objects.
  const data = freeze(structuredClone(input));
  const records = new Map(data.products.map(p => [p.id, p]));
  return Object.freeze({
    ...data,
    resolve(manufacturer, sku, market) {
      if (![manufacturer, sku, market].every(v => typeof v === 'string' && v.length <= 80)) return null;
      return records.get(`${manufacturer}:${market}:${sku}`) ?? null;
    },
    search(query = '', manufacturer = '') {
      if (typeof query !== 'string' || query.length > 200 || typeof manufacturer !== 'string'
          || (manufacturer && !MANUFACTURERS.includes(manufacturer))) return [];
      const tokens = normalizeDiscovery(query.trim()).split(/\s+/).filter(Boolean);
      return data.products.filter(p => {
        if (manufacturer && p.manufacturer !== manufacturer) return false;
        const haystack = normalizeDiscovery([data.manufacturer_names[p.manufacturer], p.sku, p.name, p.variant, p.category, p.size].join(' '));
        return tokens.every(t => haystack.includes(t));
      });
    },
    compare(ids) {
      if (!Array.isArray(ids) || ids.length < 2 || ids.length > 4
          || new Set(ids).size !== ids.length || ids.some(id => !records.has(id))) {
        throw new Error('Choose two to four distinct catalog records.');
      }
      return ids.map(id => records.get(id));
    },
  });
}

export const directory = createDirectory(inventory);
